import React, { useEffect, useMemo, useState } from "react";
import Modal from "../modal/modal";
import defaultAvatar from "../../images/196m.png";
import { TProfile } from "../../types";
import { useCreateEventFeedbackMutation } from "../../services/store/api/eventsApi";
import styles from "./participant-feedback-modal.module.css";

type Props = {
    eventId: number;
    participant: TProfile | null;
    isOpen: boolean;
    onClose: () => void;
};

const initialState = {
    boundary_violation: false,
    aggressive_behavior: false,
    fraud_or_extortion: false,
    other: false,
    other_text: "",
};

const ParticipantFeedbackModal = ({ eventId, participant, isOpen, onClose }: Props) => {
    const [form, setForm] = useState(initialState);
    const [error, setError] = useState<string | null>(null);
    const [createEventFeedback, { isLoading }] = useCreateEventFeedbackMutation();

    useEffect(() => {
        if (isOpen) {
            setForm(initialState);
            setError(null);
        }
    }, [isOpen]);

    const participantPhoto = useMemo(
        () => participant?.photos?.[0]?.url || defaultAvatar,
        [participant],
    );

    if (!isOpen || !participant) {
        return null;
    }

    const hasAnyReason = form.boundary_violation || form.aggressive_behavior || form.fraud_or_extortion || form.other;

    const handleToggle = (key: keyof typeof initialState) => {
        setForm((prev) => ({
            ...prev,
            [key]: typeof prev[key] === "boolean" ? !prev[key] : prev[key],
        }));
        setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!hasAnyReason) {
            setError("Выберите хотя бы одну причину");
            return;
        }

        const trimmedText = form.other_text.trim();

        if (trimmedText.length < 20 || trimmedText.length > 500) {
            setError("Комментарий обязателен: опишите ситуацию от 20 до 500 символов");
            return;
        }

        try {
            await createEventFeedback({
                eventId,
                target_profile_id: participant.id,
                boundary_violation: form.boundary_violation,
                aggressive_behavior: form.aggressive_behavior,
                fraud_or_extortion: form.fraud_or_extortion,
                other: form.other,
                other_text: trimmedText,
            }).unwrap();

            onClose();
        } catch (apiError: any) {
            setError(apiError?.data?.message || "Не удалось отправить отзыв");
        }
    };

    return (
        <Modal
            header="Возникли проблемы"
            onClose={onClose}
            size="LARGE"
        >
            <div className={styles.container}>
                <div className={styles.profileBox}>
                    <img src={participantPhoto} alt={participant.name} className={styles.avatar} />
                    <div className={styles.name}>{participant.name}</div>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <label className={styles.option}>
                        <input
                            className={styles.checkbox}
                            type="checkbox"
                            checked={form.boundary_violation}
                            onChange={() => handleToggle("boundary_violation")}
                        />
                        <span>Нарушение личных границ (неприемлемые предложения, домогательства)</span>
                    </label>

                    <label className={styles.option}>
                        <input
                            className={styles.checkbox}
                            type="checkbox"
                            checked={form.aggressive_behavior}
                            onChange={() => handleToggle("aggressive_behavior")}
                        />
                        <span>Агрессивное/оскорбительное поведение</span>
                    </label>

                    <label className={styles.option}>
                        <input
                            className={styles.checkbox}
                            type="checkbox"
                            checked={form.fraud_or_extortion}
                            onChange={() => handleToggle("fraud_or_extortion")}
                        />
                        <span>Мошенничество или вымогательство денег</span>
                    </label>

                    <label className={styles.option}>
                        <input
                            className={styles.checkbox}
                            type="checkbox"
                            checked={form.other}
                            onChange={() => handleToggle("other")}
                        />
                        <span>Другое</span>
                    </label>

                    <textarea
                        className={styles.textarea}
                        placeholder="Комментарий обязателен: опишите ситуацию (от 20 до 500 символов)"
                        value={form.other_text}
                        minLength={20}
                        maxLength={500}
                        required
                        onChange={(e) => {
                            setForm((prev) => ({ ...prev, other_text: e.target.value }));
                            setError(null);
                        }}
                    />

                    <div className={styles.hint}>*Этот отзыв строго конфиденциален</div>

                    {error && <div className={styles.error}>{error}</div>}

                    <div className={styles.actions}>
                        <button className={styles.submit} type="submit" disabled={isLoading}>
                            {isLoading ? "Отправка..." : "Отправить"}
                        </button>
                        <button className={styles.cancel} type="button" onClick={onClose} disabled={isLoading}>
                            Отмена
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default ParticipantFeedbackModal;
